import axios from 'axios';

export const API_BASE_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_API_URL || '/api';
const GATEWAY_URL = process.env.REACT_APP_GATEWAY_URL || '';

// Get the uploads URL from gateway (for user photos from hub)
export const getGatewayUploadsUrl = (path) => {
  if (!path) return null;
  const base = GATEWAY_URL.replace(/\/api$/, '').replace(/\/$/, '');
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  return base ? `${base}/${stripped}` : `/${stripped}`;
};

// Get the uploads URL (API root without /api suffix)
export const getUploadsUrl = (path) => {
  if (!path) return null;
  // Remove /api suffix if present to get the base URL
  const baseUrl = API_BASE_URL.replace(/\/api$/, '');
  // Handle paths that already include 'uploads/' or '/uploads/'
  const stripped = path.startsWith('/') ? path.slice(1) : path;
  const cleanPath = stripped.startsWith('uploads/') ? stripped : `uploads/${stripped}`;
  return `${baseUrl}/${cleanPath}`;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Instance axios partagée (Bearer token Keycloak appliqué via setAuthToken).
// Exportée pour permettre aux libs (hereApi.js, etc.) de réutiliser l'auth.
export { api };

// Établit/rafraîchit la session média (cookie partagé) qui autorise les <img> à charger les
// photos protégées (y compris cross-app). Throttlé pour éviter les appels répétés (setAuthToken
// est invoqué à chaque page). Non bloquant. `force` ignore le throttle : utilisé après un 401
// sur un média, quand le cookie a expiré (durée de vie 2 h, l'onglet peut rester ouvert plus).
let _lastMediaSession = 0;
export const ensureMediaSession = async (force = false) => {
  const now = Date.now();
  if (!force && now - _lastMediaSession < 60000) return;
  _lastMediaSession = now;
  try {
    await api.post('/media-session', {}, { withCredentials: true });
  } catch {
    /* non bloquant : sans effet tant que MEDIA_AUTH_ENFORCE est off */
  }
};

/**
 * Récupère un fichier de /uploads (photo, PDF, document).
 *
 * ⚠️ **`credentials: 'include'` est obligatoire** : les uploads sont servis par
 * l'API (`antennes-api.orkessi.com`) alors que le front est sur
 * `antennes.orkessi.com` — donc **cross-origin**, et `fetch()` n'envoie
 * AUCUN cookie par défaut. Sans ça, le cookie de session média ne part pas et
 * l'API répond `401 {"error":"Media session required"}` dès que
 * `MEDIA_AUTH_ENFORCE=true`. (Les `<img src>` fonctionnent, eux : une image
 * cross-origin sans attribut `crossorigin` envoie les cookies — d'où l'effet
 * trompeur « les photos passent mais pas les documents ».)
 *
 * En cas de 401, on rafraîchit la session **une fois** puis on réessaie : le
 * cookie ne vit que 2 h et un onglet peut rester ouvert bien plus longtemps.
 */
export const fetchMedia = async (url, options = {}) => {
  const opts = { ...options, credentials: 'include' };
  let resp = await fetch(url, opts);
  if (resp.status === 401) {
    await ensureMediaSession(true);
    resp = await fetch(url, opts);
  }
  return resp;
};

// Add auth token to requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    ensureMediaSession();
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired, will be handled by auth context
      console.error('Unauthorized - token may have expired');
    }
    return Promise.reject(error);
  }
);

// ============ ANTENNES ============

export const getAntennes = async (params = {}) => {
  const response = await api.get('/antennes', { params });
  return response.data;
};

export const exportAntennes = async (params = {}) => {
  const response = await api.get('/antennes/export', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'Export Antennes.xlsx');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const getAntennesStats = async () => {
  const response = await api.get('/antennes/stats');
  return response.data;
};

export const getAntennesStatsByRegion = async () => {
  const response = await api.get('/antennes/stats-by-region');
  return response.data;
};

// Generic key/value app settings
export const getSetting = async (key) => {
  const response = await api.get(`/settings/${encodeURIComponent(key)}`);
  return response.data?.data?.value ?? null;
};

export const updateSetting = async (key, value) => {
  const response = await api.put(`/settings/${encodeURIComponent(key)}`, { value });
  return response.data;
};

// ── Véhicules du contact (N par contact) ──
export const getVehiculesForContact = async (contactId) => {
  const r = await api.get(`/vehicules/contact/${contactId}`);
  return r.data?.data || [];
};
export const createVehicule = async (contactId, payload) =>
  (await api.post(`/vehicules/contact/${contactId}`, payload)).data?.data;
export const updateVehicule = async (id, payload) =>
  (await api.put(`/vehicules/${id}`, payload)).data?.data;
export const deleteVehicule = async (id) =>
  (await api.delete(`/vehicules/${id}`)).data;

// ── Prêts de bornes aux antennes ──
export const getPretsBornes = async (params = {}) =>
  (await api.get('/prets-bornes', { params })).data?.data || [];
export const getPretsBornesOptions = async () =>
  (await api.get('/prets-bornes/options')).data?.data || { bornes: [], contacts: [] };
export const createPretBorne = async (payload) =>
  (await api.post('/prets-bornes', payload)).data?.data;
export const updatePretBorne = async (id, payload) =>
  (await api.put(`/prets-bornes/${id}`, payload)).data?.data;
export const deletePretBorne = async (id) =>
  (await api.delete(`/prets-bornes/${id}`)).data;

// ── Tarifs (grille structurée — standard + personnalisations par contact) ──
export const getTarifsStandard = async () => {
  const r = await api.get('/tarifs-beta/standard');
  return r.data?.data || null;
};
export const saveTarifsStandard = async (data) => {
  const r = await api.put('/tarifs-beta/standard', { data });
  return r.data?.data || null;
};
export const getTarifsStandardHistorique = async () => {
  const r = await api.get('/tarifs-beta/standard/historique');
  return r.data?.data || [];
};
// ── Grilles tarifaires (plusieurs grilles nommées, dont une par défaut) ──
export const getTarifsGrilles = async () => {
  const r = await api.get('/tarifs-beta/grilles');
  return r.data?.data || [];
};
export const getTarifsGrille = async (id) => {
  const r = await api.get(`/tarifs-beta/grilles/${id}`);
  return r.data?.data || null;
};
export const createTarifsGrille = async ({ libelle, sourceId }) => {
  const r = await api.post('/tarifs-beta/grilles', { libelle, source_id: sourceId });
  return r.data?.data || null;
};
export const updateTarifsGrille = async (id, patch) => {
  const r = await api.put(`/tarifs-beta/grilles/${id}`, patch);
  return r.data?.data || null;
};
export const saveTarifsGrilleData = async (id, data) => {
  const r = await api.put(`/tarifs-beta/grilles/${id}/data`, { data });
  return r.data?.data || null;
};
export const deleteTarifsGrille = async (id) => {
  await api.delete(`/tarifs-beta/grilles/${id}`);
};

export const getTarifsForContact = async (contactId) => {
  const r = await api.get(`/tarifs-beta/contact/${contactId}`);
  return {
    data: r.data?.data || null,
    isCustom: !!r.data?.is_custom,
    grille: r.data?.grille || null,
  };
};
export const saveTarifsForContact = async (contactId, data) => {
  const r = await api.put(`/tarifs-beta/contact/${contactId}`, { data });
  return { data: r.data?.data || null, isCustom: !!r.data?.is_custom };
};
export const resetTarifsForContact = async (contactId) => {
  const r = await api.delete(`/tarifs-beta/contact/${contactId}`);
  return { data: r.data?.data || null, isCustom: !!r.data?.is_custom };
};
// Aliases pour compat avec l'ancien naming (au cas où d'autres fichiers les utilisent)
export const getTarifsBetaForContact = async (cid) => (await getTarifsForContact(cid)).data;
export const saveTarifsBetaForContact = async (cid, data) => (await saveTarifsForContact(cid, data)).data;

// ── Legacy : ancien système tarif (overrides lignes par contact) ──
// Conservé pour le composant ContactTarifBlock (encore affiché en
// 'Informations de facturation'). Les fonctions du standard/lignes ont
// été retirées car remplacées par le nouveau système (getTarifsStandard /
// saveTarifsStandard plus haut, basés sur la grille structurée).
// Côté contact
export const getTarifContact = async (contactId) => {
  const r = await api.get(`/tarifs-contact/${contactId}`);
  return r.data?.data || null;
};
export const setTarifContactOverride = async (contactId, ligneId, payload) =>
  (await api.put(`/tarifs-contact/${contactId}/ligne/${ligneId}`, payload)).data?.data;
export const resetTarifContactOverride = async (contactId, ligneId) =>
  (await api.delete(`/tarifs-contact/${contactId}/ligne/${ligneId}`)).data;

export const getBornes = async () => {
  const response = await api.get('/antennes/bornes');
  return response.data;
};

export const getAntennesVilles = async (params = {}) => {
  const response = await api.get('/antennes/villes', { params });
  return response.data;
};

export const getAntenne = async (id) => {
  const response = await api.get(`/antennes/${id}`);
  return response.data;
};

export const createAntenne = async (data) => {
  const response = await api.post('/antennes', data);
  return response.data;
};

export const updateAntenne = async (id, data) => {
  const response = await api.put(`/antennes/${id}`, data);
  return response.data;
};

export const deleteAntenne = async (id) => {
  const response = await api.delete(`/antennes/${id}`);
  return response.data;
};

// Contacts de la ville de l'antenne qui n'y sont pas rattachés
export const getAntenneSuggestions = async (id) => {
  const response = await api.get(`/antennes/${id}/suggestions`);
  return response.data.data || [];
};

export const rattacherContacts = async (id, contactIds) => {
  const response = await api.post(`/antennes/${id}/rattacher`, { contact_ids: contactIds });
  return response.data.data;
};

// ============ CONTACTS ============

export const getContacts = async (params = {}) => {
  const response = await api.get('/contacts', { params });
  return response.data;
};

export const getContact = async (id) => {
  const response = await api.get(`/contacts/${id}`);
  return response.data;
};

export const createContact = async (data) => {
  const response = await api.post('/contacts', data);
  return response.data;
};

export const updateContact = async (id, data) => {
  const response = await api.put(`/contacts/${id}`, data);
  return response.data;
};

// Corbeille : la suppression est logique, ces deux actions la gèrent.
export const restoreContact = async (id) => {
  const response = await api.post(`/contacts/${id}/restore`);
  return response.data;
};

export const deleteContactPermanent = async (id) => {
  const response = await api.delete(`/contacts/${id}/permanent`);
  return response.data;
};

export const deleteContact = async (id) => {
  const response = await api.delete(`/contacts/${id}`);
  return response.data;
};

export const addBinome = async (contactId, data) => {
  const response = await api.post(`/contacts/${contactId}/binomes`, data);
  return response.data;
};

export const deleteBinome = async (contactId, binomeRelationId) => {
  const response = await api.delete(`/contacts/${contactId}/binomes/${binomeRelationId}`);
  return response.data;
};

export const updateBinome = async (contactId, binomeRelationId, data) => {
  const response = await api.put(`/contacts/${contactId}/binomes/${binomeRelationId}`, data);
  return response.data;
};

export const addHistorique = async (contactId, data, files = []) => {
  if (files.length > 0) {
    const formData = new FormData();
    formData.append('type', data.type || 'commentaire');
    formData.append('contenu', data.contenu);
    files.forEach(f => formData.append('files', f));
    const response = await api.post(`/contacts/${contactId}/historique`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
  const response = await api.post(`/contacts/${contactId}/historique`, data);
  return response.data;
};

export const updateHistorique = async (contactId, historiqueId, data, newFiles = [], deleteFileIds = []) => {
  if (newFiles.length > 0 || deleteFileIds.length > 0) {
    const formData = new FormData();
    formData.append('contenu', data.contenu);
    if (deleteFileIds.length > 0) {
      formData.append('delete_file_ids', JSON.stringify(deleteFileIds));
    }
    newFiles.forEach(f => formData.append('files', f));
    const response = await api.put(`/contacts/${contactId}/historique/${historiqueId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }
  const response = await api.put(`/contacts/${contactId}/historique/${historiqueId}`, data);
  return response.data;
};

export const deleteHistorique = async (contactId, historiqueId) => {
  const response = await api.delete(`/contacts/${contactId}/historique/${historiqueId}`);
  return response.data;
};

// ============ LOOKUPS ============

export const getEtats = async () => {
  const response = await api.get('/lookups/etats');
  return response.data.data || response.data;
};

export const getLieuTypes = async () => {
  const response = await api.get('/lookups/lieu-types');
  return response.data.data || response.data;
};

export const getSecteurs = async () => {
  const response = await api.get('/lookups/secteurs');
  return response.data.data || response.data;
};

export const getPays = async () => {
  const response = await api.get('/lookups/pays');
  return response.data.data || response.data;
};

export const getTypeProfils = async (forContactAntenne) => {
  const params = forContactAntenne !== undefined ? { for_contact_antenne: forContactAntenne } : {};
  const response = await api.get('/lookups/type-profils', { params });
  return response.data.data || response.data;
};

export const getDebitInternets = async () => {
  const response = await api.get('/lookups/debit-internets');
  return response.data.data || response.data;
};

export const getContactTypeDocuments = async () => {
  const response = await api.get('/lookups/contact-type-documents');
  return response.data.data || response.data;
};

export const getQualifications = async () => {
  const response = await api.get('/lookups/qualifications');
  return response.data.data || response.data;
};

export const getLienBinomes = async () => {
  const response = await api.get('/lookups/lien-binomes');
  return response.data.data || response.data;
};

export const getGammesBornes = async () => {
  const response = await api.get('/lookups/gammes-bornes');
  return response.data.data || response.data;
};

export const getBornesDisponibles = async (gamme_borne_id) => {
  const params = gamme_borne_id ? { gamme_borne_id } : {};
  const response = await api.get('/lookups/bornes-disponibles', { params });
  return response.data.data || response.data;
};

export const getTypeEquipements = async () => {
  const response = await api.get('/lookups/type-equipements');
  return response.data.data || response.data;
};

export const getEquipements = async (typeEquipementId) => {
  const params = typeEquipementId ? { type_equipement_id: typeEquipementId } : {};
  const response = await api.get('/lookups/equipements', { params });
  return response.data.data || response.data;
};

export const getTypeStatus = async () => {
  const response = await api.get('/lookups/type-status');
  return response.data.data || response.data;
};

export const getRentrerSelfizeeOptions = async () => {
  const response = await api.get('/lookups/rentrer-selfizee');
  return response.data.data || response.data;
};

export const getVilles = async () => {
  const response = await api.get('/lookups/villes');
  return response.data.data || response.data;
};

export const getAntennesList = async () => {
  const response = await api.get('/lookups/antennes');
  return response.data.data || response.data;
};

export const getContactsList = async (params = {}) => {
  const response = await api.get('/lookups/contacts', { params });
  return response.data.data || response.data;
};

// ============ UPLOADS ============

export const uploadPhoto = async (file) => {
  const formData = new FormData();
  formData.append('photo', file);

  const response = await api.post('/uploads/photo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const uploadDocument = async (file, contactAntenneId, typeDocumentId, nom) => {
  const formData = new FormData();
  formData.append('document', file);
  formData.append('contact_antenne_id', contactAntenneId);
  if (typeDocumentId) formData.append('type_document_id', typeDocumentId);
  if (nom) formData.append('nom', nom);

  const response = await api.post('/uploads/document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteDocument = async (id) => {
  const response = await api.delete(`/uploads/document/${id}`);
  return response.data;
};

export const assignBorne = async (contactId, borneId) => {
  const response = await api.post(`/contacts/${contactId}/bornes`, { borne_id: borneId });
  return response.data;
};

export const unassignBorne = async (contactId, borneId) => {
  const response = await api.delete(`/contacts/${contactId}/bornes/${borneId}`);
  return response.data;
};

export const addAccessoire = async (contactId, data) => {
  const response = await api.post(`/contacts/${contactId}/accessoires`, data);
  return response.data;
};

export const deleteAccessoire = async (contactId, accId) => {
  const response = await api.delete(`/contacts/${contactId}/accessoires/${accId}`);
  return response.data;
};

// ============ MEDIA ============

export const mediaList = async (path) => {
  const response = await api.get('/media/list', { params: { path } });
  return response.data;
};

export const mediaCreateFolder = async (parentPath, name) => {
  const response = await api.post('/media/folder', { path: parentPath, name });
  return response.data;
};

export const mediaCheckExists = async (parentPath, filenames) => {
  const response = await api.post('/media/check-exists', { path: parentPath, filenames });
  return response.data;
};

export const mediaUpload = async (path, files, conflict = 'skip', onProgress) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const response = await api.post(`/media/upload?path=${encodeURIComponent(path)}&conflict=${conflict}`, formData, {
    headers: { 'Content-Type': null }, // let browser set multipart/form-data with boundary
    onUploadProgress: onProgress,
  });
  return response.data;
};

export const mediaDeleteFiles = async (files) => {
  const response = await api.post('/media/delete', { files });
  return response.data;
};

export const mediaMove = async (files, destination) => {
  const response = await api.post('/media/move', { files, destination });
  return response.data;
};

export const mediaRename = async (filePath, newName) => {
  const response = await api.post('/media/rename', { path: filePath, newName });
  return response.data;
};

// ============ WS CONFIG ============

export const getWsToken = async () => {
  const response = await api.get('/ws-config/token');
  return response.data;
};

export const generateWsToken = async () => {
  const response = await api.post('/ws-config/token');
  return response.data;
};

export default api;
